import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const [state, setState] = useState("polling");
  const navigate = useNavigate();
  const tries = useRef(0);

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return setState("failed");
    const poll = async () => {
      try {
        const res = await api.get(`/payments/status/${sid}`);
        if (res.data.payment_status === "paid") {
          await refresh();
          setState("paid");
          return;
        }
      } catch {}
      tries.current += 1;
      if (tries.current < 15) setTimeout(poll, 2000);
      else setState("timeout");
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="mx-auto flex max-w-md flex-col items-center px-4 py-24 text-center" data-testid="payment-success-page">
      {state === "polling" && (
        <>
          <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
          <h1 className="font-heading mt-6 text-2xl font-bold">Confirming your payment...</h1>
          <p className="mt-2 text-sm text-muted-foreground">Please wait while we verify with Stripe.</p>
        </>
      )}
      {state === "paid" && (
        <>
          <CheckCircle2 className="h-14 w-14 text-emerald-600" />
          <h1 className="font-heading mt-6 text-2xl font-bold" data-testid="payment-success-title">Membership Activated!</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Your ₹99 Owner Direct Contact Pass is live. All owner phone numbers & WhatsApp links are now unlocked across every listing.
          </p>
          <Button data-testid="browse-properties-after-payment" className="mt-6" onClick={() => navigate("/properties")}>Browse Properties</Button>
        </>
      )}
      {(state === "failed" || state === "timeout") && (
        <>
          <XCircle className="h-14 w-14 text-amber-500" />
          <h1 className="font-heading mt-6 text-2xl font-bold">Payment not confirmed yet</h1>
          <p className="mt-2 text-sm text-muted-foreground">If you completed payment, it may take a moment to reflect. Try refreshing.</p>
          <Button data-testid="payment-retry-home" variant="outline" className="mt-6" onClick={() => navigate("/")}>Go Home</Button>
        </>
      )}
    </div>
  );
}

export function PaymentCancel() {
  const navigate = useNavigate();
  const { openMembership } = useAuth();
  const location = useLocation();
  return (
    <div className="mx-auto flex max-w-md flex-col items-center px-4 py-24 text-center" data-testid="payment-cancel-page">
      <XCircle className="h-14 w-14 text-slate-400" />
      <h1 className="font-heading mt-6 text-2xl font-bold">Payment cancelled</h1>
      <p className="mt-2 text-sm text-muted-foreground">No amount was charged. You can unlock owner contacts anytime with the ₹99 pass.</p>
      <div className="mt-6 flex gap-3">
        <Button data-testid="cancel-retry-payment" onClick={() => { openMembership(); navigate("/"); }}>Try Again</Button>
        <Button data-testid="cancel-go-home" variant="outline" onClick={() => navigate("/")}>Go Home</Button>
      </div>
    </div>
  );
}
